import Component from '@glimmer/component'
import { action } from '@ember/object'
import { htmlSafe, type SafeString } from '@ember/template'
import { tracked } from '@glimmer/tracking'
import { isPresent, isEmpty } from '@ember/utils'
import { service } from '@ember/service'
import { timeout, task } from 'ember-concurrency'
import {
  tableColumnsToComparerKeyMapping,
  injectComparerKeys,
  type SortingConfig,
  type SortComparerKey,
} from '@blakeelearning/data-tables/utils/sorting'
import type { IntlService } from 'ember-intl'
import type DataTransformerService from '@blakeelearning/data-tables/services/data-transformer'
import type { PipelineDescriptor } from '@blakeelearning/data-tables/utils/data-transformer'
import type { FormatValueOptions } from 'teacher-ui-client/utils/format-value'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import TooltipCell from 'teacher-ui-client/components/stable-table/basic-cells/tooltip'
import applyTransform from '@blakeelearning/data-tables/modifiers/apply-transform'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { hash } from '@ember/helper'
import type { RowObject } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import { assert } from '@ember/debug'

interface Args {
  sortingConfig?: SortingConfig
  studentLessonData?: StudentLessonItem[]
  onSortClick?: ({ sortKey }: SortObject) => void
  exportable?: boolean
}

interface Signature {
  Args: Args
}

interface Column {
  value?: string
  cellClassNames?: string
  valuePath?: string
  sortKey?: string
  comparerKey?: SortComparerKey
  classNames?: string
  component?: typeof SortableCell | typeof TooltipCell
  tooltipValue?: string
  formatEmptyConfig?: FormatValueOptions
}

interface StudentLessonItem extends RowObject {
  average_score: number
  highest_score: number
  lesson: number
  lesson_quizzes_attempted: number
  quiz_scores: number[]
}

interface StudentLessonWithQuizScoresItem extends StudentLessonItem {
  focusSounds?: string | SafeString
  wordsRead?: string
  booksRead?: SafeString
  quizScoresJoined: string
}

export class TablesPeaksProgressStudentComponent extends Component<Signature> {
  @service intl!: IntlService

  @service lessonInfo!: LessonInfoService

  @service dataTransformer!: DataTransformerService

  @tracked transformedItems: StudentLessonItem[] = []

  /**
   * Returns an array of operation descriptions that will be applied to the items
   */
  get pipelineDescriptors() {
    return [this.rowDescriptor, this.sortingDescriptor]
  }

  get rowDescriptor() {
    const { intl } = this
    const transformer = ({ items, ...rest }: { items: StudentLessonItem[] }) => {
      return {
        ...rest,
        items: items.map((studentLessonData) => {
          const fpLessonData = this.lessonInfo
            .lessonsFor(Precinct.FP_PEAKS)
            .find((lesson) => lesson.lesson_number === studentLessonData.lesson)
          let focusSoundsText
          let wordsReadText
          let booksReadText
          if (fpLessonData) {
            const { phase, set, sounds, words, books } = fpLessonData
            assert('fast phonics lesson item missing data', sounds && words && books)

            const prefix = 'tables.peaksProgress.student.data'
            focusSoundsText = intl.t(`${prefix}.focusSounds`, {
              phase,
              set,
              focusSounds: sounds.join(','),
              htmlSafe: true,
            })
            wordsReadText = words.join(', ')
            /* Rather than simply joining by comma, we want to control the wrapping of the book titles. By placing each
             * book title in an inline block, it adds a layer to the wrapping hierarchy, so they aren't wrapped first */
            booksReadText = htmlSafe(
              books
                .map((bookTitle, index) => {
                  return `<span class="inline-block">${bookTitle}${index === books.length - 1 ? '' : ','}</span>`
                })
                .join(' '),
            )
          }
          // Format the quiz scores
          const quizScores = studentLessonData.quiz_scores || []
          const quizScoresJoined = quizScores.map((score) => `${score}%`).join(', ') // from [10,20,30] to 10%, 20%, 30%
          return {
            ...studentLessonData,
            focusSounds: focusSoundsText,
            wordsRead: wordsReadText,
            booksRead: booksReadText,
            quizScoresJoined,
          }
        }),
      }
    }
    const descriptor = { type: 'custom', transformer }
    return descriptor
  }

  get sortingDescriptor() {
    if (!this.args.sortingConfig) return null

    const comparerKeyMapping = tableColumnsToComparerKeyMapping(this.columns)
    const sortingConfigWithComparerKeys = injectComparerKeys(this.args.sortingConfig, comparerKeyMapping)
    const sortDescriptor = { type: 'sort', config: sortingConfigWithComparerKeys }

    return sortDescriptor
  }

  /**
   * This task provides an array of items and operations to the data transformer service, which will asynchronously
   * transform the data according to the operations, then set those transformed items for display in the table
   */
  @action
  performTransformItems(
    _element: unknown,
    [items, pipelineDescriptors]: [Args['studentLessonData'], PipelineDescriptor[]],
  ) {
    void this.transformItems.perform(items, pipelineDescriptors)
  }

  transformItems = task(
    { restartable: true },
    async (items: Args['studentLessonData'] = [], pipelineDescriptors: PipelineDescriptor[] = []) => {
      await timeout(200) // debounce

      const presentDescriptors = pipelineDescriptors.filter(isPresent)
      const transformedData = await this.dataTransformer.buildAndTransform<
        StudentLessonItem,
        StudentLessonWithQuizScoresItem
      >(items, presentDescriptors)

      this.transformedItems = transformedData.items
    },
  )

  get columns(): Column[] {
    const { intl } = this
    const translationPrefix = 'tables.peaksProgress.student.headers'
    const tooltipPrefix = 'tables.peaksProgress.student.tooltips'

    const defaultColumn: Column = {
      classNames: 'table-box table-box-header bg-oceany-blue-100',
      cellClassNames: 'table-box table-box-cell bg-oceany-blue-50 group-hover:bg-oceany-blue-100',
    }

    const columns: Column[] = [
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.lessonNumber`),
        valuePath: 'lesson',
        component: SortableCell,
        comparerKey: 'sortByNumber',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.focusSounds`),
        valuePath: 'focusSounds',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.wordsRead`),
        classNames: `${defaultColumn.classNames} w-[400px]`,
        valuePath: 'wordsRead',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.booksRead`),
        valuePath: 'booksRead',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.quizAttempts`),
        valuePath: 'lesson_quizzes_attempted',
        component: SortableCell,
        comparerKey: 'sortByNumber',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.quizHighestScore`),
        valuePath: 'highest_score',
        component: SortableCell,
        comparerKey: 'sortByNumber',
        formatEmptyConfig: { append: '%' },
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.quizScores`),
        valuePath: 'quizScoresJoined',
        component: TooltipCell,
        tooltipValue: intl.t(`${tooltipPrefix}.quizScores`),
      },
    ]

    return columns
  }

  get rows() {
    return this.transformedItems
  }

  get hasNoData() {
    return this.transformItems.isIdle && isEmpty(this.rows)
  }

  get noDataMessage() {
    return this.intl.t('tables.peaksProgress.emptyTableMessage')
  }

  <template>
    <div {{applyTransform this.performTransformItems @studentLessonData this.pipelineDescriptors}}>
      <BasicTable
        class="relative w-full"
        exportable={{@exportable}}
        @columns={{this.columns}}
        @rows={{this.rows}}
        @config={{hash sortingConfig=@sortingConfig onSortClick=@onSortClick}}
        as |bt|
      >
        <bt.head />

        {{! If not transforming and nothing to show, always only display the empty message }}
        {{#if this.hasNoData}}
          <bt.spanner class="p-3 text-center italic">
            {{this.noDataMessage}}
          </bt.spanner>
        {{else}}
          {{! If transforming, always show a loading spinner}}
          {{! When no items, show it normally. }}
          {{! If there are items, place it on top of the existing table body (absolute) }}
          {{#if this.transformItems.isRunning}}
            <bt.spanner
              class="table-box table-box-cell border-transparent
                {{if this.rows 'absolute left-1/2 -translate-x-1/2 transform'}}"
            >
              <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
            </bt.spanner>
          {{/if}}
          <bt.body class={{if this.transformItems.isRunning "opacity-50"}} as |b|>
            <b.row class="table-row-striped group" />
          </bt.body>
        {{/if}}

      </BasicTable>
    </div>
  </template>
}

export default TablesPeaksProgressStudentComponent
